import { useState } from "react";
import { supabase } from '../supabase';

const C = {
  bg:"#0e0e0e", sidebar:"#0a0a0a", surface:"#181818", surface2:"#202020",
  border:"#2a2a2a", muted:"#666", orange:"#FF5F1F", magenta:"#E8197D",
  gold:"#F5A623", green:"#4ade80", white:"#fff", red:"#f87171", blue:"#60a5fa",
};
const card    = (x={}) => ({ background:C.surface, borderRadius:10, padding:16, border:`0.5px solid ${C.border}`, ...x });
const inp     = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const btn     = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white}:{}), ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...(v==="danger"?{background:"#3d1515",color:C.red,border:`0.5px solid #f8717133`}:{}), ...x });
const lbl     = t => <div style={{fontSize:11,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>{t}</div>;
const gradBar = <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1,marginBottom:24}}/>;
const secTitle= t => <div style={{fontSize:11,color:C.muted,marginBottom:12,letterSpacing:0.5,textTransform:"uppercase"}}>{t}</div>;

const statusCfg = {
  confirmed: { label:"Bekreftet", color:"#4ade80", bg:"#14532d44", border:"#4ade8033" },
  inquiry:   { label:"Forespørsel", color:"#E8197D", bg:"#E8197D22", border:"#E8197D44" },
  cancelled: { label:"Kansellert", color:"#f87171", bg:"#3d151522", border:"#f8717133" },
};
const roleCfg = {
  admin:    { label:"Admin",    color:"#34d399" },
  danser:   { label:"Danser",   color:"#E8197D" },
  vokalist: { label:"Vokalist", color:"#a78bfa" },
  tm:       { label:"TM",       color:"#F5A623" },
  akrobat:  { label:"Akrobat",  color:"#FF5F1F" },
  musiker:  { label:"Musiker",  color:"#60a5fa" },
  tekniker: { label:"Tekniker", color:"#94a3b8" },
};

function ProjectCard({ p, users, onClick }) {
  const fmt = d => new Date(d+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"short"});
  return (
    <div onClick={onClick} style={{...card(),marginBottom:10,cursor:"pointer"}}
      onMouseEnter={e=>e.currentTarget.style.borderColor=C.orange}
      onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:14,fontWeight:500,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
          <div style={{fontSize:12,color:C.muted}}>{fmt(p.date)}{p.end_date&&p.end_date!==p.date?` – ${fmt(p.end_date)}`:""} · {p.location}</div>
        </div>
        <div style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:500,color:statusCfg[p.status]?.color,background:statusCfg[p.status]?.bg,border:`0.5px solid ${statusCfg[p.status]?.border}`,whiteSpace:"nowrap"}}>{statusCfg[p.status]?.label}</div>
      </div>
    </div>
  );
}

export default function CalendarPage({ user, projects, users, onSelect, availability=[], onAvailabilityChange, settings={}, isMobile }) {
  const [month, setMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [showGantt, setShowGantt] = useState(false);
  const [filterRoles, setFilterRoles] = useState([]);
  const [filterUsers, setFilterUsers] = useState([]);
  const [newBlock, setNewBlock] = useState(false);
  const [blockStart, setBlockStart] = useState("");
  const [blockEnd, setBlockEnd] = useState("");
  const [blockLabel, setBlockLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [adminTargetUser, setAdminTargetUser] = useState("");
  const [tooltip, setTooltip] = useState(null); // {x, y, text}
  const [editBlock, setEditBlock] = useState(null); // {id, start_date, end_date, label, user_id}
  const [editLabel, setEditLabel] = useState("");
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");

  const isAdmin = user.role === "admin";
  const allowArtistAvailability = settings.allow_artist_availability !== false && settings.allow_artist_availability !== "false";
  const canAddOwn = isAdmin || allowArtistAvailability;
  const visible = isAdmin ? projects : projects.filter(p => p.team.includes(user.id) && p.invites?.[user.id] !== "declined");
  const y = month.getFullYear(), m = month.getMonth();
  const offset = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();

  // Prosjekter per dag (flerdagers-eventer vises på alle dager i perioden)
  const evDays = {};
  visible.forEach(p => {
    if(!p.date) return;
    const start = new Date(p.date + "T12:00:00");
    const end = new Date((p.end_date || p.date) + "T12:00:00");
    for(let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
      if(d.getFullYear() === y && d.getMonth() === m) {
        const day = d.getDate();
        if(!evDays[day]) evDays[day] = [];
        evDays[day].push(p);
      }
    }
  });

  // Øvingstider per dag
  const rehearsalDays = {};
  visible.forEach(p => {
    (p.modules?.rehearsals_data||[]).forEach(r => {
      if(!r.date) return;
      const d = new Date(r.date+"T12:00:00");
      if(d.getFullYear()===y && d.getMonth()===m) {
        const day = d.getDate();
        if(!rehearsalDays[day]) rehearsalDays[day]=[];
        rehearsalDays[day].push({...r, projectName:p.name, projectId:p.id});
      }
    });
  });

  // To-dos med deadline per dag
  const todoDays = {};
  visible.forEach(p => {
    (p.todos||[]).filter(t=>!t.done&&t.deadline).forEach(t => {
      const d = new Date(t.deadline+"T12:00:00");
      if(d.getFullYear()===y && d.getMonth()===m) {
        const day = d.getDate();
        if(!todoDays[day]) todoDays[day]=[];
        todoDays[day].push({...t, projectName:p.name});
      }
    });
  });

  // Availability per dag
  const relevantAvail = isAdmin ? availability : availability.filter(a => a.user_id === user.id);
  const availDays = {};
  relevantAvail.forEach(a => {
    const start = new Date(a.start_date + "T12:00:00");
    const end = new Date(a.end_date + "T12:00:00");
    for(let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
      if(d.getFullYear() === y && d.getMonth() === m) {
        const day = d.getDate();
        if(!availDays[day]) availDays[day] = [];
        availDays[day].push(a);
      }
    }
  });

  const td = new Date();
  const isToday = d => d === td.getDate() && m === td.getMonth() && y === td.getFullYear();

  // Mine blokkerte perioder
  const myAvail = availability
    .filter(a => a.user_id === user.id)
    .sort((a,b) => new Date(a.start_date) - new Date(b.start_date));

  const addBlock = async () => {
    if(!blockStart || !blockEnd || !blockLabel.trim()) return;
    if(blockEnd < blockStart) return;
    setSaving(true);
    const targetId = isAdmin && adminTargetUser ? adminTargetUser : user.id;
    const { data, error } = await supabase.from('availability').insert({
      user_id: targetId,
      start_date: blockStart,
      end_date: blockEnd,
      label: blockLabel.trim()
    }).select().single();
    if(!error && data) {
      onAvailabilityChange(prev => [...prev, data]);
      setBlockStart(""); setBlockEnd(""); setBlockLabel(""); setNewBlock(false);
    }
    setSaving(false);
  };

  const deleteBlock = async (id) => {
    await supabase.from('availability').delete().eq('id', id);
    onAvailabilityChange(prev => prev.filter(a => a.id !== id));
  };

  const fmtDate = d => new Date(d + "T12:00:00").toLocaleDateString("nb-NO", {day:"numeric", month:"short"});

  // Gantt
  const activeUsers = users.filter(u => u.active);
  const filteredUsers = activeUsers.filter(u => {
    if(filterRoles.length > 0 && !filterRoles.includes(u.role)) return false;
    if(filterUsers.length > 0 && !filterUsers.includes(u.id)) return false;
    return true;
  });
  const ganttDays = Array.from({length: days}, (_,i) => i+1);

  const isBlocked = (uid, day) => {
    const dateStr = `${y}-${String(m+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    return availability.find(a => a.user_id === uid && dateStr >= a.start_date && dateStr <= a.end_date);
  };

  const hasProject = (uid, day) => {
    const dateStr = `${y}-${String(m+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    return projects.find(p => p.date && p.date <= dateStr && dateStr <= (p.end_date || p.date) && p.team.includes(uid) && p.invites?.[uid] === "accepted");
  };

  return (
    <div style={{paddingBottom: isMobile?80:0}}>
      {gradBar}

      {/* Header */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:8}}>
        <div style={{fontSize:20, fontWeight:500}}>Kalender</div>
        <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
          {isAdmin && (
            <button onClick={()=>setShowGantt(v=>!v)} style={{...btn(showGantt?"primary":"ghost"), padding:"5px 12px", fontSize:12}}>
              {showGantt ? "📅 Kalender" : "👥 Gantt"}
            </button>
          )}
          <button onClick={()=>setMonth(new Date(y,m-1,1))} style={btn("ghost",{padding:"4px 10px"})}>‹</button>
          <span style={{fontSize:13, minWidth:isMobile?100:140, textAlign:"center", textTransform:"capitalize"}}>
            {month.toLocaleDateString("nb-NO",{month:"long",year:"numeric"})}
          </span>
          <button onClick={()=>setMonth(new Date(y,m+1,1))} style={btn("ghost",{padding:"4px 10px"})}>›</button>
        </div>
      </div>

      {!showGantt && <>
        {/* Kalender-grid */}
        <div style={{...card({padding:isMobile?8:12}), marginBottom:20}}>
          <div style={{display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:isMobile?2:4, marginBottom:6}}>
            {["Ma","Ti","On","To","Fr","Lø","Sø"].map(d=>(
              <div key={d} style={{textAlign:"center", fontSize:10, color:C.muted, padding:"4px 0"}}>{d}</div>
            ))}
          </div>
          <div style={{display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:isMobile?2:4}}>
            {Array(offset).fill(null).map((_,i)=><div key={"e"+i}/>)}
            {Array(days).fill(null).map((_,i)=>{
              const day = i+1;
              const evList = evDays[day] || [];
              const avList = availDays[day] || [];
              const tod = isToday(day);
              const hasAv = avList.length > 0;
              return (
                <div key={day}
                  onClick={evList.length>0 ? ()=>onSelect(evList[0]) : undefined}
                  style={{
                    borderRadius:6,
                    padding:isMobile?"4px 2px":"4px 6px",
                    minHeight:isMobile?32:54,
                    background:tod?C.orange:evList.length>0?"#FF5F1F11":hasAv?"#ffffff06":"transparent",
                    border:tod?`1px solid ${C.orange}`:evList.length>0?`0.5px solid #FF5F1F33`:hasAv?`0.5px solid #ffffff11`:`0.5px solid transparent`,
                    cursor:evList.length>0?"pointer":"default",
                    display:"flex", flexDirection:"column", gap:2,
                  }}>
                  <div style={{fontSize:11, color:tod?"#fff":C.muted, fontWeight:tod?600:400, textAlign:"center"}}>{day}</div>
                  {!isMobile && evList.slice(0,2).map((p,pi)=>(
                    <div key={pi} style={{fontSize:9, background:`${C.orange}33`, color:C.orange, borderRadius:3, padding:"1px 4px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{p.name}</div>
                  ))}
                  {!isMobile && (rehearsalDays[day]||[]).slice(0,1).map((r,ri)=>(
                    <div key={ri} style={{fontSize:9, background:`${C.magenta}22`, color:C.magenta, borderRadius:3, padding:"1px 4px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>🕐 {r.timeFrom?`${r.timeFrom} · `:""}Øving · {r.projectName}</div>
                  ))}
                  {!isMobile && (todoDays[day]||[]).slice(0,1).map((t,ti)=>(
                    <div key={ti} style={{fontSize:9, background:`${C.blue}22`, color:C.blue, borderRadius:3, padding:"1px 4px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>✓ {t.title}</div>
                  ))}
                  {!isMobile && avList.slice(0,1).map((a,ai)=>{
                    const u = users.find(x=>x.id===a.user_id);
                    const col = roleCfg[u?.role]?.color || C.muted;
                    return <div key={ai} style={{fontSize:9, background:`${col}22`, color:col, borderRadius:3, padding:"1px 4px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>🚫 {a.label}</div>;
                  })}
                  {isMobile && evList.length>0 && <div style={{width:4,height:4,borderRadius:"50%",background:tod?"#fff":C.orange,margin:"0 auto"}}/>}
                  {isMobile && (rehearsalDays[day]||[]).length>0 && <div style={{width:4,height:4,borderRadius:"50%",background:C.magenta,margin:"0 auto"}}/>}
                  {isMobile && (todoDays[day]||[]).length>0 && <div style={{width:4,height:4,borderRadius:"50%",background:C.blue,margin:"0 auto"}}/>}
                  {isMobile && hasAv && <div style={{width:4,height:4,borderRadius:"50%",background:C.muted,margin:"0 auto"}}/>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Admin – alle blokkerte perioder */}
        {isAdmin && (
          <div style={{marginTop:24}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
              {secTitle("Alle blokkerte perioder")}
            </div>
            {availability.length === 0 && (
              <div style={{...card({textAlign:"center", padding:28})}}>
                <div style={{fontSize:13, color:C.muted}}>Ingen blokkerte perioder registrert.</div>
              </div>
            )}
            {[...availability].sort((a,b)=>new Date(a.start_date)-new Date(b.start_date)).map(a => {
              const u = users.find(x => x.id === a.user_id);
              const col = roleCfg[u?.role]?.color || C.muted;
              const isEditing = editBlock?.id === a.id;
              return (
                <div key={a.id} style={{...card({marginBottom:8, borderColor: isEditing?`${C.orange}44`:C.border})}}>
                  {isEditing ? (
                    <div>
                      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10}}>
                        <div>{lbl("Fra")}<input style={inp()} type="date" value={editStart} onChange={e=>setEditStart(e.target.value)}/></div>
                        <div>{lbl("Til")}<input style={inp()} type="date" value={editEnd} onChange={e=>setEditEnd(e.target.value)}/></div>
                      </div>
                      <div style={{marginBottom:10}}>{lbl("Beskrivelse")}<input style={inp()} value={editLabel} onChange={e=>setEditLabel(e.target.value)}/></div>
                      <div style={{display:"flex", gap:8}}>
                        <button style={btn("primary",{padding:"5px 14px",fontSize:12})} onClick={async()=>{
                          if(!editLabel.trim()||!editStart||!editEnd) return;
                          const {error} = await supabase.from('availability').update({label:editLabel.trim(),start_date:editStart,end_date:editEnd}).eq('id',a.id);
                          if(!error){
                            onAvailabilityChange(prev=>prev.map(x=>x.id===a.id?{...x,label:editLabel.trim(),start_date:editStart,end_date:editEnd}:x));
                            setEditBlock(null);
                          }
                        }}>Lagre</button>
                        <button style={btn("ghost",{padding:"5px 14px",fontSize:12})} onClick={()=>setEditBlock(null)}>Avbryt</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{display:"flex", alignItems:"center", gap:12}}>
                      <div style={{width:8, height:8, borderRadius:"50%", background:col, flexShrink:0}}/>
                      <div style={{flex:1}}>
                        <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:2}}>
                          <div style={{fontSize:13, fontWeight:500}}>{a.label}</div>
                          <div style={{fontSize:11, padding:"1px 7px", borderRadius:20, color:col, background:`${col}22`, border:`0.5px solid ${col}33`}}>{u?.name || "Ukjent"}</div>
                        </div>
                        <div style={{fontSize:11, color:C.muted}}>{fmtDate(a.start_date)} – {fmtDate(a.end_date)}</div>
                      </div>
                      <button style={btn("ghost",{padding:"4px 10px",fontSize:11})} onClick={()=>{setEditBlock(a);setEditLabel(a.label);setEditStart(a.start_date);setEditEnd(a.end_date);}}>Rediger</button>
                      <button onClick={()=>deleteBlock(a.id)} style={{background:"none", border:"none", color:"#555", cursor:"pointer", fontSize:18}}>×</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Prosjekter denne måneden */}
        {Object.keys(evDays).length > 0 && <>
          {secTitle("Oppdrag denne måneden")}
          {Object.values(evDays).flat().map(p=>(
            <ProjectCard key={p.id} p={p} users={users} onClick={()=>onSelect(p)}/>
          ))}
        </>}

        {/* Øvingstider denne måneden */}
        {Object.keys(rehearsalDays).length > 0 && <>
          {secTitle("Øvingstider denne måneden")}
          {Object.values(rehearsalDays).flat().sort((a,b)=>a.date?.localeCompare(b.date)).map((r,i)=>(
            <div key={i} onClick={()=>onSelect(visible.find(p=>p.id===r.projectId))} style={{...card({marginBottom:8}), display:"flex", alignItems:"center", gap:12, cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.borderColor=C.orange} onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
              <div style={{width:36,height:36,borderRadius:8,background:`${C.magenta}22`,border:`0.5px solid ${C.magenta}44`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <div style={{fontSize:8,color:C.magenta,fontWeight:600,letterSpacing:0.5}}>{new Date(r.date+"T12:00:00").toLocaleDateString("nb-NO",{month:"short"}).toUpperCase()}</div>
                <div style={{fontSize:14,color:C.white,fontWeight:700,lineHeight:1}}>{new Date(r.date+"T12:00:00").getDate()}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:500,color:C.white}}>{r.projectName}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>{r.timeFrom}{r.timeTo?` – ${r.timeTo}`:""}{r.location?` · ${r.location}`:""}</div>
                {r.comment&&<div style={{fontSize:12,color:C.muted,marginTop:1}}>{r.comment}</div>}
              </div>
            </div>
          ))}
        </>}

        {/* To-dos med deadline denne måneden */}
        {Object.keys(todoDays).length > 0 && <>
          {secTitle("To-dos med deadline denne måneden")}
          {Object.values(todoDays).flat().sort((a,b)=>a.deadline?.localeCompare(b.deadline)).map((t,i)=>(
            <div key={i} onClick={()=>onSelect(visible.find(p=>p.name===t.projectName))} style={{...card({marginBottom:8}), display:"flex", alignItems:"center", gap:12, cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.borderColor=C.orange} onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
              <div style={{width:36,height:36,borderRadius:8,background:`${C.blue}22`,border:`0.5px solid ${C.blue}44`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <div style={{fontSize:8,color:C.blue,fontWeight:600,letterSpacing:0.5}}>{new Date(t.deadline+"T12:00:00").toLocaleDateString("nb-NO",{month:"short"}).toUpperCase()}</div>
                <div style={{fontSize:14,color:C.white,fontWeight:700,lineHeight:1}}>{new Date(t.deadline+"T12:00:00").getDate()}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:500,color:C.white}}>{t.title}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>{t.projectName}</div>
              </div>
            </div>
          ))}
        </>}

        {/* Mine blokkerte perioder */}
        <div style={{marginTop:24}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
            {secTitle("Mine blokkerte perioder")}
            {canAddOwn && <button onClick={()=>setNewBlock(true)} style={{...btn("ghost"), padding:"4px 12px", fontSize:12, marginBottom:12}}>+ Legg til</button>}
          </div>

 {newBlock && (
  <div style={{...card({marginBottom:12, border:`0.5px solid ${C.orange}33`})}}>
    {isAdmin && (
      <div style={{marginBottom:12}}>
        {lbl("På vegne av")}
        <select style={inp()} value={adminTargetUser} onChange={e=>setAdminTargetUser(e.target.value)}>
          <option value="">– Meg selv (admin) –</option>
          {activeUsers.map(u=>(
            <option key={u.id} value={u.id}>{u.name} ({roleCfg[u.role]?.label})</option>
          ))}
        </select>
      </div>
    )}
    <div style={{display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:10, marginBottom:10}}>
      <div>{lbl("Fra")}<input style={inp()} type="date" value={blockStart} onChange={e=>setBlockStart(e.target.value)}/></div>
      <div>{lbl("Til")}<input style={inp()} type="date" value={blockEnd} onChange={e=>setBlockEnd(e.target.value)}/></div>
    </div>
    <div style={{marginBottom:12}}>
      {lbl("Beskrivelse")}
      <input style={inp()} value={blockLabel} onChange={e=>setBlockLabel(e.target.value)} placeholder="F.eks. MGP, Ferie, Annen jobb..."/>
    </div>
    <div style={{display:"flex", gap:8}}>
      <button style={btn("primary",{flex:1})} onClick={addBlock} disabled={saving}>{saving?"Lagrer...":"Lagre"}</button>
      <button style={btn("ghost")} onClick={()=>setNewBlock(false)}>Avbryt</button>
    </div>
  </div>
)}

          {myAvail.length === 0 && !newBlock && (
            <div style={{...card({textAlign:"center", padding:28})}}>
              <div style={{fontSize:13, color:C.muted}}>Ingen blokkerte perioder registrert.</div>
            </div>
          )}

          {myAvail.map(a=>(
            <div key={a.id} style={{...card({marginBottom:8}), display:"flex", alignItems:"center", gap:12}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13, fontWeight:500}}>{a.label}</div>
                <div style={{fontSize:11, color:C.muted, marginTop:2}}>{fmtDate(a.start_date)} – {fmtDate(a.end_date)}</div>
              </div>
              <button onClick={()=>deleteBlock(a.id)} style={{background:"none", border:"none", color:"#555", cursor:"pointer", fontSize:18}}>×</button>
            </div>
          ))}
        </div>
      </>}

      {tooltip && (
        <div style={{position:"fixed",left:tooltip.x+12,top:tooltip.y-8,background:"#1a1a1a",border:`0.5px solid ${C.border}`,borderRadius:6,padding:"5px 10px",fontSize:12,color:C.white,pointerEvents:"none",zIndex:999,whiteSpace:"nowrap",boxShadow:"0 4px 12px rgba(0,0,0,0.4)"}}>
          {tooltip.text}
        </div>
      )}
      {/* Gantt – kun admin */}
      {showGantt && isAdmin && (
        <div>
          {/* Filtre */}
          <div style={{...card({marginBottom:16})}}>
            {secTitle("Filtrering")}
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11, color:C.muted, marginBottom:6}}>Rolle</div>
              <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                {Object.entries(roleCfg).filter(([k])=>k!=="admin").map(([k,v])=>(
                  <button key={k} onClick={()=>setFilterRoles(prev=>prev.includes(k)?prev.filter(x=>x!==k):[...prev,k])}
                    style={{padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer",
                      border:`0.5px solid ${filterRoles.includes(k)?v.color:C.border}`,
                      background:filterRoles.includes(k)?`${v.color}22`:"transparent",
                      color:filterRoles.includes(k)?v.color:C.muted}}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{fontSize:11, color:C.muted, marginBottom:6}}>Person</div>
              <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                {activeUsers.map(u=>(
                  <button key={u.id} onClick={()=>setFilterUsers(prev=>prev.includes(u.id)?prev.filter(x=>x!==u.id):[...prev,u.id])}
                    style={{padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer",
                      border:`0.5px solid ${filterUsers.includes(u.id)?(roleCfg[u.role]?.color||C.orange):C.border}`,
                      background:filterUsers.includes(u.id)?`${roleCfg[u.role]?.color||C.orange}22`:"transparent",
                      color:filterUsers.includes(u.id)?(roleCfg[u.role]?.color||C.orange):C.muted}}>
                    {u.name.split(" ")[0]}
                  </button>
                ))}
                {(filterRoles.length>0||filterUsers.length>0) && (
                  <button onClick={()=>{setFilterRoles([]);setFilterUsers([]);}}
                    style={{padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer", border:`0.5px solid ${C.border}`, background:"transparent", color:C.red}}>
                    ✕ Nullstill
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Gantt-grid */}
          <div style={{...card({padding:0, overflow:"auto"})}}>
            <div style={{minWidth: 120 + days*24}}>
              {/* Datoheader */}
              <div style={{display:"grid", gridTemplateColumns:`120px repeat(${days},1fr)`, borderBottom:`0.5px solid ${C.border}`}}>
                <div style={{padding:"8px 12px", fontSize:10, color:C.muted}}/>
                {ganttDays.map(d=>(
                  <div key={d} style={{textAlign:"center", padding:"6px 0", fontSize:9,
                    color:isToday(d)?C.orange:C.muted,
                    fontWeight:isToday(d)?700:400,
                    borderLeft:`0.5px solid ${C.border}22`,
                    background:isToday(d)?"#FF5F1F11":"transparent"}}>
                    {d}
                  </div>
                ))}
              </div>

              {/* Prosjektrad */}
              <div style={{display:"grid", gridTemplateColumns:`120px repeat(${days},1fr)`, borderBottom:`0.5px solid ${C.border}`}}>
                <div style={{padding:"6px 12px", display:"flex", alignItems:"center", gap:6, borderRight:`0.5px solid ${C.border}`}}>
                  <div style={{width:6, height:6, borderRadius:"50%", background:C.orange, flexShrink:0}}/>
                  <div style={{fontSize:11, color:C.orange, fontWeight:500}}>Prosjekter</div>
                </div>
                {ganttDays.map(d => {
                  const dateStr = `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                  const proj = projects.find(p => p.date && p.date <= dateStr && dateStr <= (p.end_date || p.date) && !p.archived);
                  const tod = isToday(d);
                  return (
                    <div key={d}
                      style={{
                        height:28,
                        background: proj?`${C.orange}44`:tod?"#ffffff06":"transparent",
                        borderLeft:`0.5px solid ${C.border}11`,
                        borderTop: proj?`2px solid ${C.orange}`:"none",
                        cursor: proj?"pointer":"default",
                      }}
                      onMouseEnter={e=>{ if(proj) setTooltip({x:e.clientX, y:e.clientY, text:`📁 ${proj.name}`}); }}
                      onMouseMove={e=>{ if(proj) setTooltip(t=>t?{...t,x:e.clientX,y:e.clientY}:null); }}
                      onMouseLeave={()=>setTooltip(null)}
                    />
                  );
                })}
              </div>

              {/* Rader */}
              {filteredUsers.map(u => {
                const col = roleCfg[u.role]?.color || C.muted;
                return (
                  <div key={u.id} style={{display:"grid", gridTemplateColumns:`120px repeat(${days},1fr)`, borderBottom:`0.5px solid ${C.border}22`}}>
                    <div style={{padding:"6px 12px", display:"flex", alignItems:"center", gap:6, borderRight:`0.5px solid ${C.border}`}}>
                      <div style={{width:6, height:6, borderRadius:"50%", background:col, flexShrink:0}}/>
                      <div style={{fontSize:11, color:C.white, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{u.name.split(" ")[0]}</div>
                    </div>
                    {ganttDays.map(d => {
                      const blocked = isBlocked(u.id, d);
                      const proj = hasProject(u.id, d);
                      const tod = isToday(d);
                      return (
                       <div key={d}
                          style={{
                            height:28,
                            background: blocked?`${col}33`:proj?`${C.orange}44`:tod?"#ffffff06":"transparent",
                            borderLeft:`0.5px solid ${C.border}11`,
                            borderTop: blocked?`2px solid ${col}`:proj?`2px solid ${C.orange}`:"none",
                            cursor: (blocked||proj)?"pointer":"default",
                          }}
                          onMouseEnter={e=>{
                            if(blocked||proj) setTooltip({x:e.clientX, y:e.clientY, text: proj?`📁 ${proj.name}`:blocked.label});
                          }}
                          onMouseMove={e=>{
                            if(blocked||proj) setTooltip(t=>t?{...t,x:e.clientX,y:e.clientY}:null);
                          }}
                          onMouseLeave={()=>setTooltip(null)}
                        />
                      );
                    })}
                  </div>
                );
              })}

              {filteredUsers.length === 0 && (
                <div style={{padding:32, textAlign:"center", fontSize:13, color:C.muted}}>Ingen brukere matcher filteret.</div>
              )}

              {/* Legend */}
              <div style={{padding:"10px 16px", display:"flex", gap:16, alignItems:"center", borderTop:`0.5px solid ${C.border}`, flexWrap:"wrap"}}>
                <div style={{display:"flex", alignItems:"center", gap:6, fontSize:11, color:C.muted}}>
                  <div style={{width:12, height:12, borderRadius:2, background:`${C.orange}44`, borderTop:`2px solid ${C.orange}`}}/> Booket på show
                </div>
                <div style={{display:"flex", alignItems:"center", gap:6, fontSize:11, color:C.muted}}>
                  <div style={{width:12, height:12, borderRadius:2, background:`${C.magenta}33`, borderTop:`2px solid ${C.magenta}`}}/> Opptatt (rollefarger)
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}